/** A QR code as inline SVG.
 *
 *  SVG rather than a canvas or a data: URI so it stays crisp at whatever size the
 *  dialog gives it, prints properly, and needs no ref or effect to draw.
 *
 *  The four-module quiet zone is not decoration — the spec requires it, and scanners
 *  genuinely fail without it when the code sits tight against a border. It is the
 *  usual reason a hand-rolled QR "sometimes doesn't scan".
 */
import qrcode from 'qrcode-generator'

const QUIET = 4

export function QrCode({ value, size = 168, title }: {
  value: string
  size?: number
  title?: string
}) {
  // Type 0 = pick the smallest version that fits. 'M' corrects ~15%, which is the
  // usual choice for a link on a screen: 'L' is fragile against glare, and 'H' makes
  // the modules smaller for no benefit here.
  const qr = qrcode(0, 'M')
  qr.addData(value)
  qr.make()

  const n = qr.getModuleCount()
  const span = n + QUIET * 2

  // One rect per run of dark modules rather than per module: same picture, roughly a
  // third of the nodes, and it keeps the markup readable when inspected.
  const rects: string[] = []
  for (let r = 0; r < n; r++) {
    let start = -1
    for (let c = 0; c <= n; c++) {
      const dark = c < n && qr.isDark(r, c)
      if (dark && start < 0) start = c
      if (!dark && start >= 0) {
        rects.push(`M${start + QUIET},${r + QUIET}h${c - start}v1h-${c - start}z`)
        start = -1
      }
    }
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${span} ${span}`}
         shapeRendering="crispEdges" role="img"
         aria-label={title || 'QR code'}>
      {title ? <title>{title}</title> : null}
      {/* Explicit white behind the code: a transparent QR on a tinted panel is a
          common cause of a scan that will not lock on. */}
      <rect width={span} height={span} fill="#ffffff" />
      <path d={rects.join('')} fill="#111827" />
    </svg>
  )
}
